import { PlatformLocation, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, ErrorHandler, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TitleStrategy } from '@angular/router';
import AppModule from '@app/app.module';
import PageTitleStrategy from '@app/services/page-title-strategy';
import AppAuthService from '@app/shared/common/auth/app-auth.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppConsts } from '@shared/AppConsts';
import { RealtoCrmCommonModule } from '@shared/common/common.module';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { CookieConsentService } from '@shared/common/session/cookie-consent.service';
import { AppUiCustomizationService } from '@shared/common/ui/app-ui-customization.service';
import ChunkLoadErrorHandler from '@shared/helpers/chunk-load-error-handler';
import { DomHelper } from '@shared/helpers/DomHelper';
import { UrlHelper } from '@shared/helpers/UrlHelper';
import { LocaleMappingService } from '@shared/locale-mapping.service';
import { API_BASE_URL, UiCustomizationSettingsDto } from '@shared/service-proxies/service-proxies';
import { ServiceProxyModule } from '@shared/service-proxies/service-proxy.module';
import DatesService from '@shared/utils/dates-service';
import NgxBootstrapDatePickerConfigService from 'assets/ngx-bootstrap/ngx-bootstrap-datepicker-config.service';
import localForage from 'localforage';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';

import { AppPreBootstrap } from './AppPreBootstrap';
import RootComponent from './root.component';
import { RootRoutingModule } from './root-routing.module';

function initializeLocalForage() {
    localForage.config({
        driver: localForage.LOCALSTORAGE,
        name: 'RealtoCrm',
        version: 1.0,
        storeName: 'abpzerotemplate_local_storage',
        description: 'Cached data for RealtoCrm',
    });
}

function initializeAppCssClasses(injector: Injector, theme: UiCustomizationSettingsDto) {
    const appUiCustomizationService = injector.get(AppUiCustomizationService);
    appUiCustomizationService.init(theme);

    // Css classes based on the layout
    if (abp.session.userId) {
        document.body.className = appUiCustomizationService.getAppModuleBodyClass();
        document.body.setAttribute('style', appUiCustomizationService.getAppModuleBodyStyle());
    } else {
        document.body.className = appUiCustomizationService.getAccountModuleBodyClass();
        document.body.setAttribute('style', appUiCustomizationService.getAccountModuleBodyStyle());
    }
}

function initializeTenantResources(injector: Injector) {
    const appSessionService: AppSessionService = injector.get(AppSessionService);

    if (appSessionService.tenant?.customCssId) {
        document.head.appendChild(DomHelper.createElement('link', [
            {
                key: 'id',
                value: 'TenantCustomCss',
            },
            {
                key: 'rel',
                value: 'stylesheet',
            },
            {
                key: 'href',
                value:
                    AppConsts.remoteServiceBaseUrl +
                    '/TenantCustomization/GetCustomCss?tenantId=' +
                    appSessionService.tenant.id,
            },
        ]));
    }

    const metaImage = DomHelper.getElementByAttributeValue('meta', 'property', 'og:image');
    if (metaImage) {
        //set og share image meta tag
        if (!appSessionService.tenant?.HasLogo()) {
            metaImage.setAttribute(
                'content',
                window.location.origin +
                '/assets/common/images/app-logo-on-' +
                abp.setting.get(appSessionService.theme.baseSettings.theme + '.' + 'App.UiManagement.Left.AsideSkin') +
                '.svg',
            );
        } else {
            metaImage.setAttribute(
                'content',
                AppConsts.remoteServiceBaseUrl + '/TenantCustomization/GetLogo?tenantId=' + appSessionService.tenant.id,
            );
        }
    }
}

function initializeCookieConsent(injector: Injector) {
    const cookieConsentService: CookieConsentService = injector.get(CookieConsentService);
    cookieConsentService.init();
}

function getDocumentOrigin() {
    if (!document.location.origin) {
        return (
            document.location.protocol +
            '//' +
            document.location.hostname +
            (document.location.port
                ? ':' + document.location.port
                : '')
        );
    }

    return document.location.origin;
}

function shouldLoadLocale(): boolean {
    return abp.localization.currentLanguage.name && abp.localization.currentLanguage.name !== 'en-US';
}

function convertAbpLocaleToAngularLocale(locale: string): string {
    return new LocaleMappingService()
        .map('angular', locale);
}

function loadAngularLocale(locale: string): Promise<any> {
    switch (locale) {
        case 'bg': return import('@angular/common/locales/bg');
        case 'pt': return import('@angular/common/locales/pt');
        case 'zh': return import('@angular/common/locales/zh');
        case 'he': return import('@angular/common/locales/he');
        case 'es': return import('@angular/common/locales/es');
        default: return import('@angular/common/locales/en');
    }
}

function registerLocales(
    resolve: (value?: boolean | Promise<boolean>) => void,
    reject: any,
    spinnerService: NgxSpinnerService,
) {
    if (shouldLoadLocale()) {
        const angularLocale = convertAbpLocaleToAngularLocale(abp.localization.currentLanguage.name);
        loadAngularLocale(angularLocale)
            .then((module) => {
                registerLocaleData(module.default);
                NgxBootstrapDatePickerConfigService.registerNgxBootstrapDatePickerLocales()
                    .then(() => {
                        resolve(true);
                        spinnerService.hide();
                    });
            }, reject);
    } else {
        NgxBootstrapDatePickerConfigService.registerNgxBootstrapDatePickerLocales()
            .then(() => {
                resolve(true);
                spinnerService.hide();
            });
    }
}

function getRemoteServiceBaseUrl(): string {
    return AppConsts.remoteServiceBaseUrl;
}

function getCurrentLanguage(): string {
    return convertAbpLocaleToAngularLocale(abp.localization.currentLanguage.name);
}

function setCurrentLanguageCookie() {
    const fiveYears = new Date(new Date()
        .getTime() + 5 * 365 * 86400000);

    abp.utils.setCookieValue(
        'Abp.Localization.CultureName',
        abp.localization.currentLanguage.name,
        fiveYears,
        abp.appPath,
    );
}

function getCurrencyCode(injector: Injector): string {
    const appSessionService: AppSessionService = injector.get(AppSessionService);
    return appSessionService.application.currency;
}

function getBaseHref(platformLocation: PlatformLocation): string {
    const baseUrl = platformLocation.getBaseHrefFromDOM();
    if (baseUrl) {
        return baseUrl;
    }

    return '/';
}

function handleLogoutRequest(authService: AppAuthService) {
    const currentUrl = UrlHelper.initialUrl;
    const returnUrl = UrlHelper.getReturnUrl();
    if (currentUrl.indexOf('account/logout') >= 0 && returnUrl) {
        authService.logout(true, returnUrl);
    }
}

function appInitializerFactory(injector: Injector, platformLocation: PlatformLocation) {
    return () => {
        const spinnerService = injector.get(NgxSpinnerService);

        spinnerService.show();

        return new Promise<boolean>((resolve, reject) => {
            AppConsts.appBaseHref = getBaseHref(platformLocation);
            const appBaseUrl = getDocumentOrigin() + AppConsts.appBaseHref;

            initializeLocalForage();

            AppPreBootstrap.run(
                appBaseUrl,
                injector,
                () => {
                    setCurrentLanguageCookie();

                    handleLogoutRequest(injector.get(AppAuthService));

                    const appSessionService: AppSessionService = injector.get(AppSessionService);
                    appSessionService.init()
                        .then(
                            (result) => {
                                initializeAppCssClasses(injector, result);
                                initializeTenantResources(injector);
                                initializeCookieConsent(injector);
                                registerLocales(resolve, reject, spinnerService);
                            },
                            (err) => {
                                spinnerService.hide();
                                reject(err);
                            },
                        );
                },
                resolve,
                reject,
            );
        });
    };
}

@NgModule({
    declarations: [RootComponent],
    bootstrap: [RootComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        AppModule,
        RealtoCrmCommonModule.forRoot(),
        ServiceProxyModule,
        RootRoutingModule,
        NgxSpinnerModule,
        NgbModule], providers: [
        { provide: API_BASE_URL, useFactory: getRemoteServiceBaseUrl },
        { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [Injector, PlatformLocation],
            multi: true,
        },
        {
            provide: LOCALE_ID,
            useFactory: getCurrentLanguage,
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useFactory: getCurrencyCode,
            deps: [Injector],
        },
        { provide: TitleStrategy, useClass: PageTitleStrategy },
        DatesService,
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
class RootModule {
}

export {
    appInitializerFactory,
    shouldLoadLocale,
    convertAbpLocaleToAngularLocale,
    getRemoteServiceBaseUrl,
    getCurrentLanguage,
    getCurrencyCode,
    getBaseHref,
    RootModule,
};

export default RootModule;
